/**
 * Trunnion AI - Execution Replay
 * VCR-style step-through viewer for completed/failed executions.
 *
 * Layout:
 *  - Top section: Read-only step cards (horizontal scrollable) with the active step
 *    highlighted in gold.
 *  - Middle section: Current step detail — agent name, status, reasoning trace (collapsed),
 *    tool calls, and output.
 *  - Bottom section: ReplayTimeline + ReplayControls.
 */

import { useEffect, useState } from 'react';
import {
  Bot,
  ChevronDown,
  ChevronRight,
  Clock,
  Wrench,
} from 'lucide-react';
import { useReplayStore } from '@/stores/replayStore';
import { ReplayTimeline } from './ReplayTimeline';
import { ReplayControls } from './ReplayControls';
import { ToolCallCard } from './ToolCallCard';
import { ReasoningTraceViewer } from './ReasoningTraceViewer';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { EXECUTION_STATUS_CONFIG } from '@/config/constants';
import type { Execution } from '@/types';

interface ExecutionReplayProps {
  /** The completed/failed execution to replay */
  execution: Execution;
}

/**
 * Return the Badge variant that corresponds to a given execution status.
 */
function statusToBadgeVariant(
  status: string,
): 'success' | 'error' | 'info' | 'warning' | 'default' {
  switch (status) {
    case 'completed':
      return 'success';
    case 'failed':
    case 'timed_out':
    case 'cancelled':
      return 'error';
    case 'running':
    case 'waiting_approval':
      return 'info';
    case 'paused':
    case 'pending':
      return 'warning';
    default:
      return 'default';
  }
}

export function ExecutionReplay({ execution }: ExecutionReplayProps) {
  const loadReplay = useReplayStore((s) => s.loadReplay);
  const reset = useReplayStore((s) => s.reset);
  const steps = useReplayStore((s) => s.steps);
  const currentStepIndex = useReplayStore((s) => s.currentStepIndex);
  const seekTo = useReplayStore((s) => s.seekTo);

  const [showReasoning, setShowReasoning] = useState(false);
  const [showToolCalls, setShowToolCalls] = useState(true);
  const [showOutput, setShowOutput] = useState(false);

  // Load steps into the replay store when the execution changes
  useEffect(() => {
    loadReplay(execution.steps);
    return () => reset();
  }, [execution.id, execution.steps, loadReplay, reset]);

  const currentStep = steps[currentStepIndex] ?? null;
  const statusConfig = currentStep
    ? EXECUTION_STATUS_CONFIG[currentStep.status]
    : null;

  return (
    <div className="flex flex-col h-full">
      {/* ---------------------------------------------------------------
       * TOP SECTION: Step overview cards (horizontal scroll)
       * --------------------------------------------------------------- */}
      <div className="border-b border-trunnion-slate-700 bg-trunnion-slate-900/60 px-4 py-3 overflow-x-auto">
        <div className="flex gap-2 min-w-max">
          {steps.map((step, index) => {
            const isActive = index === currentStepIndex;
            const stepStatusConfig = EXECUTION_STATUS_CONFIG[step.status];

            return (
              <button
                key={step.id}
                onClick={() => seekTo(index)}
                className={`flex flex-col items-start rounded-lg border px-3 py-2 min-w-35 max-w-45 text-left transition-all ${
                  isActive
                    ? 'border-trunnion-gold-500 bg-trunnion-gold-500/10 shadow-[0_0_12px_rgba(234,179,8,0.15)]'
                    : 'border-trunnion-slate-700 bg-trunnion-slate-800/50 hover:border-trunnion-slate-600'
                }`}
              >
                <div className="flex items-center gap-1.5 w-full">
                  <Bot
                    className={`h-3.5 w-3.5 shrink-0 ${
                      isActive
                        ? 'text-trunnion-gold-500'
                        : 'text-trunnion-slate-400'
                    }`}
                  />
                  <span
                    className={`text-xs font-medium truncate ${
                      isActive ? 'text-trunnion-gold-400' : 'text-white'
                    }`}
                  >
                    {step.agentName ?? step.nodeName}
                  </span>
                </div>
                <div className="mt-1 flex items-center gap-1.5">
                  <Badge
                    variant={statusToBadgeVariant(step.status)}
                    dot
                  >
                    {stepStatusConfig?.label ?? step.status}
                  </Badge>
                </div>
                <span className="mt-1 text-[10px] text-trunnion-slate-500">
                  Step {index + 1}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* ---------------------------------------------------------------
       * MIDDLE SECTION: Current step detail
       * --------------------------------------------------------------- */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
        {!currentStep ? (
          <div className="flex items-center justify-center h-full text-sm text-trunnion-slate-400">
            No step selected
          </div>
        ) : (
          <>
            {/* Step header card */}
            <Card
              padding="md"
              className="border-trunnion-gold-500/30 bg-trunnion-gold-500/5"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-trunnion-gold-500/20">
                    <Bot className="h-4 w-4 text-trunnion-gold-500" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-white">
                      {currentStep.agentName ?? currentStep.nodeName}
                    </h3>
                    <p className="text-xs text-trunnion-slate-400">
                      {currentStep.nodeType} node &middot; {currentStep.nodeName}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <Badge variant={statusToBadgeVariant(currentStep.status)} dot>
                    {statusConfig?.label ?? currentStep.status}
                  </Badge>
                  {currentStep.startedAt && (
                    <span className="flex items-center gap-1 text-xs text-trunnion-slate-500">
                      <Clock className="h-3 w-3" />
                      {new Date(currentStep.startedAt).toLocaleTimeString()}
                    </span>
                  )}
                </div>
              </div>

              {/* Token usage summary */}
              {currentStep.tokenUsage && (
                <div className="mt-3 flex gap-4 text-xs text-trunnion-slate-400">
                  <span>
                    Prompt:{' '}
                    <span className="text-trunnion-slate-200">
                      {currentStep.tokenUsage.promptTokens.toLocaleString()}
                    </span>
                  </span>
                  <span>
                    Completion:{' '}
                    <span className="text-trunnion-slate-200">
                      {currentStep.tokenUsage.completionTokens.toLocaleString()}
                    </span>
                  </span>
                  <span>
                    Total:{' '}
                    <span className="text-white font-medium">
                      {currentStep.tokenUsage.totalTokens.toLocaleString()}
                    </span>
                  </span>
                </div>
              )}

              {/* Error message */}
              {currentStep.error && (
                <div className="mt-3 rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-400">
                  {currentStep.error}
                </div>
              )}
            </Card>

            {/* Reasoning Trace (collapsed by default) */}
            <div>
              <button
                onClick={() => setShowReasoning(!showReasoning)}
                className="flex w-full items-center gap-2 rounded-lg border border-trunnion-slate-700 bg-trunnion-slate-800/50 px-3 py-2 text-left transition-colors hover:border-trunnion-slate-600"
              >
                {showReasoning ? (
                  <ChevronDown className="h-4 w-4 text-trunnion-slate-400" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-trunnion-slate-400" />
                )}
                <span className="text-sm font-medium text-trunnion-slate-200">
                  Reasoning Trace
                </span>
                <Badge variant="default">
                  {currentStep.reasoningTrace.length}
                </Badge>
              </button>
              {showReasoning && (
                <div className="mt-2">
                  <ReasoningTraceViewer entries={currentStep.reasoningTrace} />
                </div>
              )}
            </div>

            {/* Tool Calls */}
            <div>
              <button
                onClick={() => setShowToolCalls(!showToolCalls)}
                className="flex w-full items-center gap-2 rounded-lg border border-trunnion-slate-700 bg-trunnion-slate-800/50 px-3 py-2 text-left transition-colors hover:border-trunnion-slate-600"
              >
                {showToolCalls ? (
                  <ChevronDown className="h-4 w-4 text-trunnion-slate-400" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-trunnion-slate-400" />
                )}
                <Wrench className="h-3.5 w-3.5 text-trunnion-gold-500" />
                <span className="text-sm font-medium text-trunnion-slate-200">
                  Tool Calls
                </span>
                <Badge variant="default">
                  {currentStep.toolCalls.length}
                </Badge>
              </button>
              {showToolCalls && (
                <div className="mt-2 space-y-3">
                  {currentStep.toolCalls.length === 0 ? (
                    <p className="text-center py-4 text-sm text-trunnion-slate-400">
                      No tool calls for this step
                    </p>
                  ) : (
                    currentStep.toolCalls.map((tc) => (
                      <ToolCallCard key={tc.id} toolCall={tc} />
                    ))
                  )}
                </div>
              )}
            </div>

            {/* Output (collapsed by default) */}
            {currentStep.output && (
              <div>
                <button
                  onClick={() => setShowOutput(!showOutput)}
                  className="flex w-full items-center gap-2 rounded-lg border border-trunnion-slate-700 bg-trunnion-slate-800/50 px-3 py-2 text-left transition-colors hover:border-trunnion-slate-600"
                >
                  {showOutput ? (
                    <ChevronDown className="h-4 w-4 text-trunnion-slate-400" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-trunnion-slate-400" />
                  )}
                  <span className="text-sm font-medium text-trunnion-slate-200">
                    Step Output
                  </span>
                </button>
                {showOutput && (
                  <div className="mt-2 rounded-lg border border-trunnion-slate-700 bg-trunnion-slate-800/30 p-3">
                    <pre className="text-xs text-trunnion-slate-200 whitespace-pre-wrap overflow-x-auto max-h-64">
                      {JSON.stringify(currentStep.output, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* ---------------------------------------------------------------
       * BOTTOM SECTION: Timeline + Controls
       * --------------------------------------------------------------- */}
      <div className="border-t border-trunnion-slate-700 bg-trunnion-slate-900/80 px-4 py-3 space-y-3">
        <ReplayTimeline />
        <ReplayControls />
      </div>
    </div>
  );
}
